import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DebtType } from "../../Models/Credit";
import { ROUTE_PATHS } from "../../Routes/constants";
import {
  BulkSnoozeDatesResponse,
  getBulkSnoozeDates,
} from "../../Services/SnoozeService";
import { sendEvent } from "../../Utils/analytics";
import { getRemoteBoolean, getRemoteValue } from "../../Utils/firebase";
import useAuthState from "../../Utils/store";
import AllDebts from "../AllDebts/AllDebts";
import CTab from "../CTab/CTab";
import PaymentsSelectionBar from "../PaymentsSelectionBar/PaymentsSelectionBar";
import BulkSnoozeFlow from "../TotalDebts/BulkSnoozeFlow";

const DebtsTab = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const setPaymentMethod = useAuthState((state) => state.setPaymentMethod);
  const setIsAllPayment = useAuthState((state) => state.setIsAllPayment);

  // CRL-3241: Remote Config `enable_payment_multi_select` (default false)
  const isPaymentMultiSelectEnabled = getRemoteBoolean(
    "enable_payment_multi_select",
    false
  );

  // CRL-3241 multi-select state (pending tab only; gated by remote flag)
  const [selectedDebts, setSelectedDebts] = useState<Map<number, DebtType>>(
    new Map()
  );
  const [pendingTotalCount, setPendingTotalCount] = useState<number | null>(null);

  // Snooze of the selected payments (design `bulk-defer`): reuses the bulk
  // snooze flow with only the selected payments as scope.
  const [snoozeAvailability, setSnoozeAvailability] =
    useState<BulkSnoozeDatesResponse | undefined>(undefined);
  const [snoozeStartSignal, setSnoozeStartSignal] = useState(0);
  /** Frozen scope for the running flow so mid-flow deselects don't change it. */
  const [snoozeScope, setSnoozeScope] = useState<DebtType[]>([]);

  const getSnoozeAvailability = useCallback(async () => {
    const response = await getBulkSnoozeDates();
    setSnoozeAvailability(response);
  }, []);

  useEffect(() => {
    void getSnoozeAvailability();
  }, [getSnoozeAvailability]);

  const onToggleSelect = (debt: DebtType) => {
    setSelectedDebts((prev) => {
      const next = new Map(prev);
      if (next.has(debt.id)) {
        next.delete(debt.id);
      } else {
        next.set(debt.id, debt);
      }
      return next;
    });
  };

  const selectedList = useMemo(() => Array.from(selectedDebts.values()), [selectedDebts]);
  const selectedIds = useMemo(() => Array.from(selectedDebts.keys()), [selectedDebts]);
  const selectedTotal = useMemo(
    () => selectedList.reduce((sum, d) => sum + (d.remainingPaymentAmount || 0), 0),
    [selectedList]
  );
  const selectedSnoozable = useMemo(
    () => selectedList.filter((d) => d.snoozeAvailable),
    [selectedList]
  );

  // Pay maps onto existing flows only: single credit, or every pending payment.
  // Arbitrary subsets need a bulk payment endpoint (see ASSUMPTIONS.md #18).
  const isAllSelected =
    pendingTotalCount !== null &&
    pendingTotalCount > 0 &&
    selectedDebts.size === pendingTotalCount;
  const payEnabled = selectedDebts.size === 1 || isAllSelected;

  const snoozeEnabled =
    selectedSnoozable.length > 0 &&
    snoozeAvailability?.isSnoozeAvailable === true;

  const handlePaySelected = () => {
    if (!payEnabled) return;
    sendEvent(`click_pay_selected_payments_button`, {
      selected_count: selectedDebts.size,
      total_amount: selectedTotal,
    });
    if (selectedDebts.size === 1) {
      const debt = selectedList[0];
      setPaymentMethod({ type: 2, creditId: debt.creditId });
      setIsAllPayment(false);
    } else {
      setPaymentMethod({ type: 0 });
      setIsAllPayment(true);
    }
    navigate(ROUTE_PATHS.CHECKOUT);
  };

  const handleSnoozeSelected = () => {
    if (!snoozeEnabled) return;
    setSnoozeScope(selectedList);
    setSnoozeStartSignal((s) => s + 1);
    sendEvent("view_bulk_snooze_calendar_page", {
      total_credit_count: selectedList.length,
      snoozable_credit_count: selectedSnoozable.length,
      not_snoozable_credit_count:
        selectedList.length - selectedSnoozable.length,
    });
  };

  const DebtsTabProperty = [
    {
      id: 0,
      value: "allDebts",
      label: getRemoteValue("all_debts"),
      component: (currentTab: number) => (
        <AllDebts
          currentTab={currentTab}
          selectable={isPaymentMultiSelectEnabled}
          selectedIds={isPaymentMultiSelectEnabled ? selectedIds : undefined}
          onToggleSelect={
            isPaymentMultiSelectEnabled ? onToggleSelect : undefined
          }
          onTotalCount={
            isPaymentMultiSelectEnabled ? setPendingTotalCount : undefined
          }
        />
      ),
      path: "#pending",
    },
    {
      id: 1,
      value: "completedDebts",
      label: getRemoteValue("past_expenses"),
      component: (currentTab: number) => <AllDebts currentTab={currentTab} />,
      path: "#completed",
    },
  ];

  const isCompletedTab = location.hash === "#completed";

  return (
    <>
      <CTab sx={{ mx: 1 }} isNavigatePath TabProperty={DebtsTabProperty} />
      {isPaymentMultiSelectEnabled && !isCompletedTab && (
        <PaymentsSelectionBar
          count={selectedDebts.size}
          totalAmount={selectedTotal}
          payEnabled={payEnabled}
          onClear={() => setSelectedDebts(new Map())}
          onPay={handlePaySelected}
          snoozeEnabled={snoozeEnabled}
          snoozePartial={
            selectedSnoozable.length > 0 &&
            selectedSnoozable.length < selectedDebts.size
          }
          onSnooze={handleSnoozeSelected}
          items={selectedList}
          onRemove={onToggleSelect}
        />
      )}
      <BulkSnoozeFlow
        startSignal={snoozeStartSignal}
        payments={snoozeScope}
        snoozeAvailability={snoozeAvailability}
      />
    </>
  );
};

export default DebtsTab;
